/**
 * Multirotor airframe configuration: schema, a hand-rolled validator (no
 * dependency), and a loader. Values in `config/drone.*.json` are starter
 * numbers — see the `// TODO tune` notes there.
 */
import type { Vec3 } from './mathx';
import { parseSensorConfig, type SensorConfig } from './sensorTypes';

export interface MotorConfig {
  /** motor velocity constant (RPM per volt) — informational for now */
  kv: number;
  /** maximum steady-state speed, RPM */
  maxRpm: number;
  /** thrust coefficient with air density folded in: T = ct · ω² (N per (rad/s)²) */
  ct: number;
  /** reaction-torque coefficient: Q = cq · ω² (N·m per (rad/s)²) */
  cq: number;
  /** first-order spin-up time constant, seconds */
  timeConstant: number;
}

export interface DragConfig {
  /** linear term k1 in F = -(k1·v + k2·|v|·v) */
  linear: number;
  /** quadratic term k2 */
  quadratic: number;
}

export interface BatteryConfig {
  cells: number;
  capacityMah: number;
  internalResistanceOhm: number;
  restingVoltagePerCell: number;
}

export interface MotorMount {
  /** motor position in the body (FLU) frame, metres */
  position: Vec3;
  /** +1 for a CCW-spinning prop, -1 for CW (sets the reaction-torque sign) */
  spin: 1 | -1;
}

export interface VehicleConfig {
  name: string;
  massKg: number;
  /** diagonal inertia in the body principal frame, kg·m² */
  inertia: Vec3;
  armLengthM: number;
  propRadiusM: number;
  /** exactly 4 motors */
  motors: MotorMount[];
  motor: MotorConfig;
  drag: DragConfig;
  battery: BatteryConfig;
  /** IMU/GPS/baro/rangefinder noise model; defaults applied when absent. */
  sensors: SensorConfig;
}

export class VehicleConfigError extends Error {
  constructor(message: string) {
    super(`vehicle config: ${message}`);
    this.name = 'VehicleConfigError';
  }
}

const isRecord = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null && !Array.isArray(x);

const num = (obj: Record<string, unknown>, key: string, path: string): number => {
  const val = obj[key];
  if (typeof val !== 'number' || !Number.isFinite(val)) {
    throw new VehicleConfigError(`${path}.${key} must be a finite number`);
  }
  return val;
};

const posNum = (obj: Record<string, unknown>, key: string, path: string): number => {
  const val = num(obj, key, path);
  if (val <= 0) throw new VehicleConfigError(`${path}.${key} must be > 0`);
  return val;
};

const vec3Of = (raw: unknown, path: string): Vec3 => {
  if (!isRecord(raw)) throw new VehicleConfigError(`${path} must be an object {x,y,z}`);
  return { x: num(raw, 'x', path), y: num(raw, 'y', path), z: num(raw, 'z', path) };
};

export const parseVehicleConfig = (raw: unknown): VehicleConfig => {
  if (!isRecord(raw)) throw new VehicleConfigError('root must be an object');

  const name = raw.name;
  if (typeof name !== 'string' || name.length === 0) {
    throw new VehicleConfigError('name must be a non-empty string');
  }

  if (!('inertia' in raw)) throw new VehicleConfigError('missing "inertia"');
  const inertia = vec3Of(raw.inertia, 'inertia');
  if (inertia.x <= 0 || inertia.y <= 0 || inertia.z <= 0) {
    throw new VehicleConfigError('inertia components must all be > 0');
  }

  const motorsRaw = raw.motors;
  if (!Array.isArray(motorsRaw) || motorsRaw.length !== 4) {
    throw new VehicleConfigError('motors must be an array of exactly 4 entries');
  }
  const motors: MotorMount[] = motorsRaw.map((m, i) => {
    if (!isRecord(m)) throw new VehicleConfigError(`motors[${i}] must be an object`);
    const spin = m.spin;
    if (spin !== 1 && spin !== -1) {
      throw new VehicleConfigError(`motors[${i}].spin must be 1 or -1`);
    }
    return { position: vec3Of(m.position, `motors[${i}].position`), spin };
  });

  const motorRaw = raw.motor;
  if (!isRecord(motorRaw)) throw new VehicleConfigError('missing "motor" object');
  const motor: MotorConfig = {
    kv: posNum(motorRaw, 'kv', 'motor'),
    maxRpm: posNum(motorRaw, 'maxRpm', 'motor'),
    ct: posNum(motorRaw, 'ct', 'motor'),
    cq: posNum(motorRaw, 'cq', 'motor'),
    timeConstant: posNum(motorRaw, 'timeConstant', 'motor'),
  };

  const dragRaw = raw.drag;
  if (!isRecord(dragRaw)) throw new VehicleConfigError('missing "drag" object');
  const drag: DragConfig = {
    linear: num(dragRaw, 'linear', 'drag'),
    quadratic: num(dragRaw, 'quadratic', 'drag'),
  };
  if (drag.linear < 0 || drag.quadratic < 0) {
    throw new VehicleConfigError('drag terms must be >= 0');
  }

  const batteryRaw = raw.battery;
  if (!isRecord(batteryRaw)) throw new VehicleConfigError('missing "battery" object');
  const battery: BatteryConfig = {
    cells: posNum(batteryRaw, 'cells', 'battery'),
    capacityMah: posNum(batteryRaw, 'capacityMah', 'battery'),
    internalResistanceOhm: posNum(batteryRaw, 'internalResistanceOhm', 'battery'),
    restingVoltagePerCell: posNum(batteryRaw, 'restingVoltagePerCell', 'battery'),
  };

  return {
    name,
    massKg: posNum(raw, 'massKg', 'root'),
    inertia,
    armLengthM: posNum(raw, 'armLengthM', 'root'),
    propRadiusM: posNum(raw, 'propRadiusM', 'root'),
    motors,
    motor,
    drag,
    battery,
    sensors: parseSensorConfig(raw.sensors),
  };
};
